import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { Mail, Inbox, Send, Archive, Search, Sparkles, Briefcase, Bell, Building2, ListTodo, Loader2, ArrowLeft, Bot, AlertTriangle, BellOff } from "lucide-react";

const BASE = import.meta.env.BASE_URL;

const FOLDERS = [
  { key: "inbox", label: "Inbox", Icon: Inbox },
  { key: "escalations", label: "Founder escalations", Icon: AlertTriangle },
  { key: "sent", label: "Sent", Icon: Send },
  { key: "archived", label: "Archived", Icon: Archive },
] as const;

const TABS = [
  { key: "all", label: "All" },
  { key: "action", label: "Action" },
  { key: "intel", label: "Intel" },
] as const;

function agentColor(a: string) {
  return ({
    kavya: "bg-pink-500/15 text-pink-300 border-pink-500/30",
    meera: "bg-[#FF5C00]/15 text-[#FF5C00] border-[#FF5C00]/30",
    viren: "bg-purple-500/15 text-purple-300 border-purple-500/30",
    admin: "bg-blue-500/15 text-blue-300 border-blue-500/30",
    system: "bg-white/[0.06] text-white/55 border-white/15",
    banker: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  } as any)[a] || "bg-white/[0.06] text-white/55 border-white/15";
}
function agentInitial(a: string) {
  return ({ kavya: "K", meera: "M", viren: "V", admin: "A", system: "•", banker: "Y" } as any)[a] || "?";
}
function relTime(d: string | Date) {
  const ms = Date.now() - new Date(d).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d`;
  return new Date(d).toLocaleDateString();
}

interface EscalationRow {
  id: number; eventKind: string; severity: "info" | "action" | "at_risk" | "breach";
  title: string; body: string | null; link: string | null; companyId: number | null;
  createdAt: string; readAt: string | null; muted: boolean;
}

export default function BankerMail() {
  const [, navigate] = useLocation();
  const [folder, setFolder] = useState<"inbox" | "escalations" | "sent" | "archived">("inbox");
  const [escalations, setEscalations] = useState<EscalationRow[]>([]);
  const [escLoading, setEscLoading] = useState(false);
  const [muting, setMuting] = useState<number | null>(null);
  const [tab, setTab] = useState<"all" | "action" | "intel">("all");
  const [threads, setThreads] = useState<any[]>([]);
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [thread, setThread] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [threadLoading, setThreadLoading] = useState(false);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState("");
  const [showList, setShowList] = useState(true); // mobile toggle
  const scrollRef = useRef<HTMLDivElement>(null);

  const loadThreads = async () => {
    const token = localStorage.getItem("banker_token");
    if (!token) return;
    setLoading(true);
    try {
      const r = await fetch(`${BASE}api/banker/me/threads?folder=${folder}&tab=${tab}`, { headers: { "x-banker-token": token } });
      if (r.ok) setThreads(await r.json());
    } catch {}
    setLoading(false);
  };

  const loadThread = async (key: string) => {
    const token = localStorage.getItem("banker_token") || "";
    setThreadLoading(true);
    try {
      const r = await fetch(`${BASE}api/banker/me/threads/${encodeURIComponent(key)}`, { headers: { "x-banker-token": token } });
      if (r.ok) setThread(await r.json());
    } catch {}
    setThreadLoading(false);
    setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }), 50);
  };

  const loadEscalations = async () => {
    const token = localStorage.getItem("banker_token");
    if (!token) return;
    setEscLoading(true);
    try {
      const r = await fetch(`${BASE}api/banker/escalations`, { headers: { "x-banker-token": token } });
      if (r.ok) {
        const j = await r.json();
        setEscalations(j.items || []);
      }
    } catch {}
    setEscLoading(false);
  };

  const muteEscalation = async (id: number) => {
    const token = localStorage.getItem("banker_token") || "";
    setMuting(id);
    try {
      const r = await fetch(`${BASE}api/banker/escalations/${id}/mute`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-banker-token": token },
        body: JSON.stringify({}),
      });
      if (r.ok) await loadEscalations();
      else {
        const j = await r.json().catch(() => ({}));
        alert(j.message || j.error || "Could not mute.");
      }
    } catch {}
    setMuting(null);
  };

  useEffect(() => {
    if (folder === "escalations") loadEscalations();
    else loadThreads();
    /* eslint-disable-next-line */
  }, [folder, tab]);
  useEffect(() => { if (activeKey) loadThread(activeKey); }, [activeKey]);

  const sendReply = async () => {
    if (!reply.trim() || !activeKey) return;
    const token = localStorage.getItem("banker_token") || "";
    setSending(true);
    try {
      await fetch(`${BASE}api/banker/me/threads/${encodeURIComponent(activeKey)}/reply`, {
        method: "POST", headers: { "Content-Type": "application/json", "x-banker-token": token },
        body: JSON.stringify({ body: reply.trim() }),
      });
      setReply("");
      await loadThread(activeKey);
      await loadThreads();
    } catch {}
    setSending(false);
  };

  const archiveThread = async () => {
    if (!thread) return;
    const token = localStorage.getItem("banker_token") || "";
    // archive every event-type message in the current thread
    const notifIds = thread.messages.filter((m: any) => m.type === "event").map((m: any) => m.notifId);
    await Promise.all(notifIds.map((id: number) =>
      fetch(`${BASE}api/banker/me/notifications/${id}`, {
        method: "PATCH", headers: { "Content-Type": "application/json", "x-banker-token": token },
        body: JSON.stringify({ archived: true }),
      })
    ));
    setActiveKey(null); setThread(null);
    await loadThreads();
  };

  const totalUnread = threads.reduce((s, t) => s + (t.unread || 0), 0);
  const filtered = search
    ? threads.filter(t => (t.subject + " " + t.preview + " " + (t.company?.name || "")).toLowerCase().includes(search.toLowerCase()))
    : threads;

  const onPickThread = (key: string) => {
    setActiveKey(key);
    setShowList(false);
  };

  return (
    <div className="h-[calc(100vh-7rem)] md:h-[calc(100vh-6rem)] -mx-2 md:mx-0 flex flex-col">
      {/* Top bar */}
      <div className="flex items-center justify-between gap-3 px-3 md:px-1 pb-3 flex-shrink-0">
        <div className="flex items-center gap-2">
          <Mail className="w-6 h-6 text-[#FF5C00]" />
          <h1 className="text-2xl md:text-3xl font-bold text-white">Mail</h1>
          {totalUnread > 0 && <span className="bg-[#FF5C00] text-white text-[11px] font-bold rounded-full min-w-[22px] h-[22px] flex items-center justify-center px-1.5">{totalUnread}</span>}
        </div>
        <div className="hidden md:flex items-center gap-1.5 text-xs">
          {FOLDERS.map(f => (
            <button key={f.key} onClick={() => { setFolder(f.key); setActiveKey(null); setThread(null); }}
              className={`px-3 py-1.5 rounded-full border flex items-center gap-1.5 ${folder === f.key ? "bg-[#FF5C00]/[0.12] border-[#FF5C00]/40 text-[#FF5C00]" : "bg-white/[0.04] border-white/[0.1] text-white/65 hover:border-white/[0.2]"}`}>
              <f.Icon className="w-3.5 h-3.5" /> {f.label}
            </button>
          ))}
        </div>
      </div>

      {folder === "escalations" ? (
        <div className="flex-1 min-h-0 bg-white/[0.03] border border-white/[0.08] rounded-2xl overflow-y-auto p-3">
          {escLoading ? (
            <div className="p-3 space-y-2">{[...Array(5)].map((_, i) => <div key={i} className="h-16 bg-white/[0.04] rounded-lg animate-pulse" />)}</div>
          ) : escalations.length === 0 ? (
            <div className="p-12 text-center">
              <AlertTriangle className="w-10 h-10 text-white/15 mx-auto mb-3" />
              <p className="text-white/45 text-sm">No founder escalations.</p>
              <p className="text-white/30 text-xs mt-2">Tara will surface missed check-ins, no-readiness-movement, and SLA breaches here as they happen.</p>
            </div>
          ) : (
            <ul className="space-y-2">
              {escalations.map(e => {
                const sevColor =
                  e.severity === "breach" ? "border-red-500/40 bg-red-500/[0.08]" :
                  e.severity === "at_risk" ? "border-amber-500/40 bg-amber-500/[0.08]" :
                  e.severity === "action" ? "border-[#FF5C00]/40 bg-[#FF5C00]/[0.08]" :
                  "border-white/[0.1] bg-white/[0.04]";
                const sevLabel =
                  e.severity === "breach" ? "BREACH" :
                  e.severity === "at_risk" ? "AT RISK" :
                  e.severity === "action" ? "ACTION" : "INFO";
                return (
                  <li key={e.id} className={`border rounded-xl p-3 ${sevColor} ${e.muted ? "opacity-60" : ""}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[10px] font-bold tracking-wider text-white/85">{sevLabel}</span>
                          <span className="text-[10px] uppercase tracking-wider text-white/45 font-mono">{e.eventKind}</span>
                          {e.muted && <span className="text-[10px] uppercase tracking-wider text-white/45 flex items-center gap-1"><BellOff className="w-3 h-3" /> muted</span>}
                        </div>
                        <p className="text-white text-sm font-semibold mt-1">{e.title}</p>
                        {e.body && <p className="text-white/70 text-xs mt-1 whitespace-pre-wrap">{e.body}</p>}
                        <p className="text-[10px] text-white/40 mt-2 font-mono">{relTime(e.createdAt)}</p>
                      </div>
                      <div className="flex flex-col gap-1.5 flex-shrink-0">
                        {e.companyId && (
                          <button onClick={() => navigate(`/banker/my-founders`)} className="text-[11px] text-[#FF5C00] hover:underline">Open →</button>
                        )}
                        {e.severity !== "breach" && !e.muted && (
                          <button
                            onClick={() => muteEscalation(e.id)}
                            disabled={muting === e.id}
                            className="text-[11px] text-white/60 hover:text-white border border-white/[0.15] rounded px-2 py-0.5 flex items-center gap-1 disabled:opacity-40">
                            {muting === e.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <BellOff className="w-3 h-3" />}
                            Mute
                          </button>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      ) : (
      <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-[360px_1fr] gap-3 px-1">
        {/* Thread list */}
        <div className={`${showList ? "flex" : "hidden md:flex"} flex-col min-h-0 bg-white/[0.03] border border-white/[0.08] rounded-2xl overflow-hidden`}>
          <div className="p-3 border-b border-white/[0.06] flex-shrink-0">
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search mail..."
                className="w-full bg-white/[0.04] border border-white/[0.1] focus:border-[#FF5C00]/40 rounded-lg pl-9 pr-3 py-2 text-xs text-white placeholder-white/30 outline-none" />
            </div>
            {folder === "inbox" && (
              <div className="flex mt-2 gap-1">
                {TABS.map(tb => (
                  <button key={tb.key} onClick={() => { setTab(tb.key); setActiveKey(null); setThread(null); }}
                    className={`flex-1 px-2 py-1 rounded text-[11px] border ${tab === tb.key ? "bg-[#FF5C00]/[0.12] border-[#FF5C00]/40 text-[#FF5C00]" : "bg-white/[0.04] border-white/[0.1] text-white/65 hover:border-white/[0.2]"}`}>
                    {tb.label}
                  </button>
                ))}
              </div>
            )}
            <div className="flex md:hidden mt-2 gap-1">
              {FOLDERS.map(f => (
                <button key={f.key} onClick={() => { setFolder(f.key); setActiveKey(null); setThread(null); }}
                  className={`flex-1 px-2 py-1.5 rounded text-[11px] border flex items-center justify-center gap-1 ${folder === f.key ? "bg-[#FF5C00]/[0.12] border-[#FF5C00]/40 text-[#FF5C00]" : "bg-white/[0.04] border-white/[0.1] text-white/65"}`}>
                  <f.Icon className="w-3 h-3" /> {f.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto">
            {loading ? (
              <div className="p-3 space-y-2">{[...Array(5)].map((_, i) => <div key={i} className="h-16 bg-white/[0.04] rounded-lg animate-pulse" />)}</div>
            ) : filtered.length === 0 ? (
              <div className="p-12 text-center">
                <Inbox className="w-10 h-10 text-white/15 mx-auto mb-3" />
                <p className="text-white/45 text-sm">{search ? "No matches." : `No mail in ${folder}.`}</p>
              </div>
            ) : (
              filtered.map(t => {
                const active = activeKey === t.key;
                const isUnread = t.unread > 0;
                return (
                  <button key={t.key} onClick={() => onPickThread(t.key)}
                    className={`w-full text-left px-3 py-3 border-b border-white/[0.04] flex items-start gap-2.5 transition-colors ${active ? "bg-[#FF5C00]/[0.08]" : "hover:bg-white/[0.04]"}`}>
                    <div className={`w-9 h-9 rounded-full border flex items-center justify-center flex-shrink-0 font-semibold text-xs ${agentColor(t.agent)}`}>
                      {agentInitial(t.agent)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-2">
                        <p className={`text-sm truncate ${isUnread ? "text-white font-semibold" : "text-white/75"}`}>{t.agentLabel}</p>
                        <span className="text-[10px] text-white/40 font-mono flex-shrink-0">{relTime(t.lastAt)}</span>
                      </div>
                      <p className={`text-xs truncate mt-0.5 ${isUnread ? "text-white/90" : "text-white/65"}`}>{t.subject}</p>
                      <p className="text-[11px] text-white/45 truncate mt-0.5">{t.preview}</p>
                      <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                        {t.company && <span className="text-[9px] uppercase tracking-wider text-white/45 bg-white/[0.05] border border-white/[0.08] rounded px-1.5 py-0.5 flex items-center gap-1"><Building2 className="w-2.5 h-2.5" />{t.company.name}</span>}
                        {t.taskId && <span className="text-[9px] uppercase tracking-wider text-white/45 bg-white/[0.05] border border-white/[0.08] rounded px-1.5 py-0.5 flex items-center gap-1"><ListTodo className="w-2.5 h-2.5" />Task #{t.taskId}</span>}
                        {t.unread > 0 && <span className="bg-[#FF5C00] text-white text-[9px] font-bold rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-1">{t.unread}</span>}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Thread detail */}
        <div className={`${!showList ? "flex" : "hidden md:flex"} flex-col min-h-0 bg-white/[0.03] border border-white/[0.08] rounded-2xl overflow-hidden`}>
          {!activeKey ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
              <Mail className="w-12 h-12 text-white/15 mb-3" />
              <p className="text-white/55 text-sm">Pick a conversation from the left.</p>
              <p className="text-white/35 text-xs mt-2 max-w-sm">Everything from your AI team — Kavya, Meera, Viren — and admin lands here. Reply and the right agent picks it up.</p>
            </div>
          ) : threadLoading || !thread ? (
            <div className="flex-1 flex items-center justify-center"><Loader2 className="w-5 h-5 text-white/40 animate-spin" /></div>
          ) : (
            <>
              <div className="px-5 py-4 border-b border-white/[0.08] flex items-start justify-between gap-3 flex-shrink-0">
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  <button onClick={() => setShowList(true)} className="md:hidden text-white/60 hover:text-white p-1 -ml-1"><ArrowLeft className="w-4 h-4" /></button>
                  <div className={`w-10 h-10 rounded-full border flex items-center justify-center flex-shrink-0 font-semibold text-sm ${agentColor(thread.agent)}`}>
                    {agentInitial(thread.agent)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-white font-semibold text-base truncate">{thread.subject}</p>
                    <p className="text-white/55 text-xs mt-0.5">
                      with <span className="text-white/80 font-medium">{thread.agentLabel}</span>
                      {thread.company && <> · <button onClick={() => navigate(`/banker/companies/${thread.company.id}`)} className="text-[#FF5C00] hover:underline">{thread.company.name}</button></>}
                    </p>
                  </div>
                </div>
                <button onClick={archiveThread} title="Archive thread" className="text-white/45 hover:text-white p-2 rounded-lg hover:bg-white/[0.05] flex-shrink-0">
                  <Archive className="w-4 h-4" />
                </button>
              </div>

              <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-3">
                {thread.messages.map((m: any) => {
                  const isMe = m.fromRole === "banker";
                  return (
                    <div key={m.id} className={`flex gap-2.5 ${isMe ? "flex-row-reverse" : ""}`}>
                      <div className={`w-7 h-7 rounded-full border flex items-center justify-center flex-shrink-0 font-semibold text-[10px] mt-0.5 ${agentColor(m.fromRole)}`}>
                        {agentInitial(m.fromRole)}
                      </div>
                      <div className={`max-w-[78%] ${isMe ? "items-end" : "items-start"} flex flex-col`}>
                        <div className="flex items-center gap-1.5 mb-1">
                          <span className="text-[11px] text-white/55 font-medium">{m.fromLabel}</span>
                          <span className="text-[10px] text-white/30 font-mono">{relTime(m.createdAt)}</span>
                        </div>
                        <div className={`rounded-2xl px-3.5 py-2.5 border ${isMe ? "bg-[#FF5C00]/[0.12] border-[#FF5C00]/30 rounded-tr-sm" : "bg-white/[0.05] border-white/[0.08] rounded-tl-sm"}`}>
                          {m.type === "event" && m.title && <p className="text-white text-sm font-medium mb-1">{m.title}</p>}
                          {m.body && <p className="text-white/85 text-sm whitespace-pre-wrap leading-relaxed">{m.body}</p>}
                          {m.kind && (
                            <p className="text-[10px] uppercase tracking-wider text-white/35 mt-2 font-mono">{m.kind.replace(/_/g, " ")}</p>
                          )}
                          {m.link && (
                            <button onClick={() => navigate(m.link)} className="mt-2 text-[11px] text-[#FF5C00] hover:underline">Open →</button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {thread.messages.length === 0 && (
                  <p className="text-white/40 text-sm text-center py-8">No messages yet in this thread.</p>
                )}
              </div>

              <div className="border-t border-white/[0.08] p-3 flex-shrink-0">
                <div className="bg-white/[0.04] border border-white/[0.1] rounded-xl p-2">
                  <textarea value={reply} onChange={(e) => setReply(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) sendReply(); }}
                    rows={2} placeholder={`Reply to ${thread.agentLabel}...`}
                    className="w-full bg-transparent text-sm text-white placeholder-white/35 outline-none resize-none px-2 py-1.5" />
                  <div className="flex items-center justify-between mt-1.5">
                    <p className="text-[10px] text-white/40 px-2 flex items-center gap-1">
                      <Bot className="w-3 h-3" /> {thread.agentLabel} will pick this up and act based on what's in this thread.
                    </p>
                    <button onClick={sendReply} disabled={!reply.trim() || sending}
                      className="bg-[#FF5C00] hover:bg-[#E55200] disabled:opacity-40 disabled:cursor-not-allowed text-white px-4 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5">
                      {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                      Send
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      )}
    </div>
  );
}
